import React from 'react';
import {
  Card, Group, Text, Badge, Stack, Skeleton,
  ActionIcon, Tooltip, Button, Divider, Flex
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import {
  IconStar, IconStarFilled, IconChevronLeft, IconChevronRight, IconChevronDown
} from '@tabler/icons-react';
import { getFixtures } from '../../../services/FootballService';
import { useDispatch, useSelector } from 'react-redux';
import { isValidValue, setFormFieldValue } from '../../../helpers/FormHelper';
import CompetitionServiceInstance from '../../../services/CompetitionService';
import { setFavouriteCompetitions } from '../../../store/slices/MemberSlice';
import { Avatar, notification } from 'antd';
import { useMediaQuery } from '@mantine/hooks';

const CDN_TEAM_LOGO = (id) => `https://media.api-sports.io/football/teams/${id}.png`;
const CDN_LEAGUE_LOGO = (id) => `https://media.api-sports.io/football/leagues/${id}.png`;

const groupByLeague = (items = []) => {
  const map = new Map();
  for (const f of items) {
    const id = f.league?.id;
    if (!id) continue;
    if (!map.has(id)) {
      map.set(id, {
        leagueId: id,
        leagueName: f.league?.name,
        country: f.league?.country,
        logo: CDN_LEAGUE_LOGO(id),
        fixtures: [],
      });
    }
    map.get(id).fixtures.push(f);
  }
  return Array.from(map.values());
};

const sortGroups = (groups, favSet, favoriteLeagueIds) => {
  const idx = (id) => {
    const n = favoriteLeagueIds.indexOf(Number(id));
    return n === -1 ? Number.MAX_SAFE_INTEGER : n;
  };
  return groups.sort((a, b) => {
    const af = favSet.has(Number(a.leagueId));
    const bf = favSet.has(Number(b.leagueId));
    if (af !== bf) return af ? -1 : 1;
    const ai = idx(a.leagueId), bi = idx(b.leagueId);
    if (ai !== bi) return ai - bi;
    return (a.leagueName || '').localeCompare(b.leagueName || '');
  });
};

const isFinished = (s) => ['FT', 'AET', 'PEN'].includes(s || '');
const isLive = (s) => ['1H', '2H', 'HT'].includes(s || '');
const kickoffText = (iso) => {
  const d = dayjs(iso);
  return `${d.format('DD.MM')} | ${d.format('HH:mm')}`;
};

function MatchRow({ f, index, onDetails }) {
  const status = f.fixture?.status?.short ?? 'NS';
  const homeName = f.teams?.home?.name;
  const awayName = f.teams?.away?.name;
  const homeId = f.teams?.home?.id;
  const awayId = f.teams?.away?.id;
  const fid = f.fixture?.id;

  const homeGoals = f.goals?.home ?? f.score?.fulltime?.home ?? null;
  const awayGoals = f.goals?.away ?? f.score?.fulltime?.away ?? null;

  const homeWin = (isFinished(status) || isLive(status)) && homeGoals > awayGoals;
  const awayWin = (isFinished(status) || isLive(status)) && awayGoals > homeGoals;
  const draw = (isFinished(status) || isLive(status)) && homeGoals === awayGoals;

  return (
    <div className={`px-match ${index % 2 === 0 ? 'is-even' : 'is-odd'}`} style={{ borderBottom: '1px solid #dadada' }} role="row">
      <div className="px-col time" role="cell">
        <Text size="xs" c="dimmed">{kickoffText(f.fixture?.date)}</Text>
        <Badge size="xs" variant="light" mt={4}>{status || 'NS'}</Badge>
      </div>

      <div className="px-col teams" role="cell">
        <div className="team">
          <Avatar src={CDN_TEAM_LOGO(homeId)} size={20} radius="sm" />
          <Text size="sm" fw={500} className="truncate">{homeName}</Text>
        </div>
        <div className="team away">
          <Avatar src={CDN_TEAM_LOGO(awayId)} size={20} radius="sm" />
          <Text size="sm" fw={500} className="truncate">{awayName}</Text>
        </div>
      </div>

      <div className="px-col teams" role="cell">
        <div className="team">
          {isFinished(status) || isLive(status) ? (<Text fw={homeWin ? 900 : draw ? 700 : 600}>{homeGoals}</Text>) : ''}
        </div>
        <div className="team away">
          {isFinished(status) || isLive(status) ? (<Text fw={awayWin ? 900 : draw ? 700 : 600}>{awayGoals}</Text>) : ''}
        </div>
      </div>

      <div className="px-col actions" role="cell">
        <Group gap={6} wrap="nowrap">
          <Button size="xs" variant="light" onClick={() => onDetails?.(fid, f)}>
            Details
          </Button>
        </Group>
      </div>
    </div>
  );
}

const Context = React.createContext({ name: 'Default' });

export default function FilteredFixtures({
  rapidKey,
  season,
  onOpenMatch,
}) {
  const token = useSelector((state) => state.Auth.token);
  const favourites = useSelector((state) => state.Member.favouriteCompetitions) ?? [];
  const coverageCompetitions = useSelector((state) => state.Utility.coverageCompetitions);
  const favoriteLeagueIds = React.useMemo(
    () => (coverageCompetitions ?? []).map((it) => Number(it?.competitionId)).filter(Boolean),
    [coverageCompetitions]
  );
  const favoriteLeagueIdSet = React.useMemo(() => new Set(favoriteLeagueIds), [favoriteLeagueIds]);
  const [date, setDate] = React.useState(new Date());
  const [mode, setMode] = React.useState('UPCOMING');
  const [upcoming, setUpcoming] = React.useState(null);
  const [live, setLive] = React.useState(null);
  const [done, setDone] = React.useState(null);

  const isXs = useMediaQuery('(max-width: 520px)');

  const dispatch = useDispatch();

  const [api, contextHolder] = notification.useNotification();
  const openNotification = (placement) => {
    api.info({
      message: `Notification alert!`,
      description: <Context.Consumer>{({ name }) => `You should login to set leagues to favourite!`}</Context.Consumer>,
      placement,
    });
  };

  const toggleFavLeague = async (leagueId, isFavourite) => {
    const isValidToken = isValidValue(token);
    if (isValidToken === false) {
      openNotification('bottomRight');
      return;
    }

    const competitionId = setFormFieldValue(coverageCompetitions?.find((item) => item?.competitionId === leagueId)?.id);
    if (isFavourite === true) {
      const response = await CompetitionServiceInstance.removeFavourite(token, competitionId);
      if (response?.data?.success) {
        const updated = favourites.filter((item) => item?.id !== Number(competitionId));
        dispatch(setFavouriteCompetitions(updated));
      }
    } else {
      const response = await CompetitionServiceInstance.setFavourite(token, competitionId);
      if (response?.data?.success) {
        const newFavouriteCompetition = response.data?.result?.competition;
        dispatch(setFavouriteCompetitions([...favourites, newFavouriteCompetition]));
      }
    }
  };

  const shiftDate = (days) => setDate((d) => dayjs(d).add(days, 'day').toDate());

  const fetchByDate = React.useCallback(async (dateStr) => {
    const all = await getFixtures({
      key: rapidKey,
      params: { date: dateStr, season, timezone: 'UTC' },
    }).catch(() => []);
    return all.filter((x) => favoriteLeagueIds.includes(Number(x?.league?.id)));
  }, [rapidKey, season, favoriteLeagueIds]);

  const fetchLive = React.useCallback(async () => {
    const all = await getFixtures({
      key: rapidKey,
      params: { live: 'all', season },
    }).catch(() => []);
    return all.filter((x) => favoriteLeagueIds.includes(Number(x?.league?.id)));
  }, [rapidKey, season, favoriteLeagueIds]);

  React.useEffect(() => {
    let stop = false;
    const fmt = dayjs(date).format('YYYY-MM-DD');

    (async () => {
      const all = await fetchByDate(fmt);
      if (stop) return;
      setUpcoming(all.filter((x) => (x.fixture?.status?.short ?? 'NS') === 'NS'));
      setDone(all.filter((x) => isFinished(x.fixture?.status?.short)));
    })();

    (async () => {
      const liveNow = await fetchLive();
      if (!stop) setLive(liveNow);
    })();

    return () => { stop = true; };
  }, [fetchByDate, fetchLive, season, date, favoriteLeagueIds]);

  const currentItems = mode === 'UPCOMING' ? upcoming : mode === 'LIVE' ? live : done;

  const renderGroups = (items) => {
    if (!items) {
      return (
        <Stack gap="xs">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={86} radius="md" />)}
        </Stack>
      );
    }
    if (items.length === 0) {
      return <Text c="dimmed" ta="center" my="md">No fixtures</Text>;
    }

    const groups = sortGroups(groupByLeague(items), favoriteLeagueIdSet, favoriteLeagueIds);

    return (
      <Stack gap="sm">
        {contextHolder}
        {groups.map((g) => {
          const fav = isValidValue(favourites.find((item) => item?.competitionId === Number(g.leagueId)));
          return (
            <Card key={g.leagueId} withBorder radius="md" p={0} className="px-group">
              <div className="px-group-header">
                <Group gap={8} wrap="nowrap">
                  <Avatar src={g.logo} size={22} radius="sm" />
                  <Text fw={800} size="sm">{g.leagueName}</Text>
                  <Badge size="xs" variant="light">{g.fixtures.length}</Badge>
                </Group>

                <Group gap={6}>
                  <Tooltip label={fav ? 'Unfavorite league' : 'Favorite league'}>
                    <ActionIcon onClick={() => toggleFavLeague(g.leagueId, fav)} variant="subtle" aria-label="favorite league">
                      {fav ? <IconStarFilled size={16} color="#f59e0b" /> : <IconStar size={16} />}
                    </ActionIcon>
                  </Tooltip>
                  <ActionIcon variant="subtle" aria-label="collapse">
                    <IconChevronDown size={16} />
                  </ActionIcon>
                </Group>
              </div>

              <Divider m={0} />
              <div role="table">
                {g.fixtures.map((f, idx) => (
                  <MatchRow
                    key={f.fixture?.id}
                    f={f}
                    index={idx}
                    onDetails={(fid) => onOpenMatch ? onOpenMatch(fid, f) : window.location.assign(`/match/${fid}`)}
                  />
                ))}
              </div>
            </Card>
          );
        })}
      </Stack>
    );
  };

  return (
    <>
      <Card withBorder radius="md" mb="sm" p="sm">
        <Flex className="px-toolbar" align="center" justify="space-between" gap="xs" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <Button size="xs" variant={mode === 'UPCOMING' ? 'filled' : 'light'} onClick={() => setMode('UPCOMING')}>
              Scheduled
            </Button>
            <Button size="xs" variant={mode === 'LIVE' ? 'filled' : 'light'} onClick={() => setMode('LIVE')}>
              Live
            </Button>
            <Button size="xs" variant={mode === 'COMPLETED' ? 'filled' : 'light'} onClick={() => setMode('COMPLETED')}>
              Finished
            </Button>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Previous day">
              <ActionIcon variant="light" className='date-changer' aria-label="Previous day" onClick={() => shiftDate(-1)}>
                <IconChevronLeft size={18} />
              </ActionIcon>
            </Tooltip>

            <DatePickerInput
              value={date}
              onChange={setDate}
              size="xs"
              dropdownType="modal"
              clearable={false}
              valueFormat={isXs ? 'DD MMM YYYY' : 'DD MMMM YYYY'}
              monthLabelFormat={isXs ? 'MMM YYYY' : 'MMMM YYYY'}
              style={{ width: '100%' }}
            />

            <Tooltip label="Next day">
              <ActionIcon variant="light" aria-label="Next day" onClick={() => shiftDate(1)}>
                <IconChevronRight size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Flex>
      </Card>

      {renderGroups(currentItems)}

      <style>{`
        .px-group {
          overflow: hidden;
          border: 1px solid rgba(2,6,23,.08);
          box-shadow: 0 4px 18px rgba(0,0,0,.05);
          background: #fff;
        }
        .px-group-header {
          display:flex; align-items:center; justify-content:space-between;
          padding: 10px 12px;
          background: linear-gradient(180deg,#ffffff,#fafafa);
        }

        /* Toolbar: keep single row on all widths; allow gentle horizontal scroll if needed */
        .px-toolbar {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }

        .px-match {
          display: grid;
          grid-template-columns: 110px 1fr 60px 100px; /* base for desktop */
          gap: 8px;
          align-items: center;
          padding: 10px 12px;
        }

        /* Medium screens */
        @media (max-width: 720px) {
          .px-match {
            grid-template-columns: 100px 1fr 50px 90px;
          }
        }

        /* Small screens */
        @media (max-width: 520px) {
          .px-match {
            grid-template-columns: 80px 1fr 40px 80px; /* shrink but keep 4 columns */
            gap: 4px;
          }
          .px-col.time { font-size: 11px; }
          .px-col.actions button { font-size: 11px; padding: 0 6px; }
        }

        .px-match.is-even { background: #fff; }
        .px-match.is-odd  { background: #f2f8f9; }
        .px-col.time { display:flex; flex-direction:column; }
        .px-col.teams { display:grid; grid-template-columns: 1fr; gap: 6px; }
        .px-col.actions { display:flex; align-items:center; justify-content:flex-end; gap: 10px; flex-wrap:nowrap; }
        .team { display:flex; align-items:center; gap:8px; min-width:0; }
        .truncate { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .score { min-width: 64px; justify-content:flex-end; }
      `}</style>
    </>
  );
}
